import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, ArrowRight, Loader2, ListPlus } from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";
import {
  KIND_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
} from "@/pages/journey/leadOptions";
import {
  Field,
  FieldRow,
  FormSection,
  inputClass,
  textareaClass,
} from "@/components/journey/LeadFormPrimitives";

const EMPTY = {
  // Contact
  first_name: "", last_name: "", company: "", role: "",
  email: "", phone: "", website: "",
  // Classification
  kind: "standort", source_type: "manual", source_label: "",
  city_region: "", system_interests: "", target_group: "",
  timeline: "", priority: "normal", tags_input: "",
  // Standort
  has_location: "", location_type: "", available_area: "",
  ceiling_height: "", current_status: "",
  // Gründer
  wish_region: "", operator_experience: "", founder_role: "",
  location_search_status: "",
  // Dev / Sales (lightweight)
  product_interest: "", message: "",
  // Context
  note_text: "", next_step: "",
  follow_up_title: "", follow_up_at: "",
};

export default function LeadNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const showStandort = form.kind === "standort";
  const showGruender = form.kind === "gruender";
  const showDev = form.kind === "dev_program";
  const showSales = form.kind === "sales_partner";

  const payload = useMemo(() => {
    const out = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );
    if (form.tags_input?.trim()) {
      out.tags = form.tags_input.split(",").map((t) => t.trim()).filter(Boolean);
    }
    delete out.tags_input;
    return out;
  }, [form]);

  const submit = async (after) => {
    const token = readToken();
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/admin/crm/leads`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 25000,
      });
      const id = r.data?.lead?.id;
      toast.success("Lead erfasst");
      if (after === "detail" && id) {
        navigate(`/admin/journey/leads/${id}`);
      } else {
        navigate("/admin/journey/inbox");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : typeof detail === "string"
        ? detail
        : err?.message || "Unbekannter Fehler";
      setError(message);
      toast.error("Konnte Lead nicht speichern");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="journey-lead-new" className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/journey/inbox"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-100"
          data-testid="lead-new-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Zurück zur Inbox
        </Link>
        <Link
          to="/admin/journey/quick-add"
          className="text-xs text-zinc-400 transition hover:text-zinc-100"
          data-testid="lead-new-switch-quick"
        >
          Quick Add ↗
        </Link>
      </div>

      <header className="space-y-1">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-400/80">
          <ListPlus className="h-3.5 w-3.5" />
          Lead erfassen — Vollmodus
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">Manuellen Lead anlegen</h1>
        <p className="text-sm text-zinc-400">
          Detaillierte Erfassung am Desktop. Keine automatische E-Mail.
        </p>
      </header>

      {error ? (
        <div
          data-testid="lead-new-error"
          className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-3 text-xs text-red-200"
        >
          {error}
        </div>
      ) : null}

      <FormSection title="1. Kontakt" testId="lead-new-section-contact">
        <FieldRow>
          <Field label="Vorname">
            <input type="text" value={form.first_name} onChange={update("first_name")} data-testid="lead-new-first-name" className={inputClass} />
          </Field>
          <Field label="Nachname">
            <input type="text" value={form.last_name} onChange={update("last_name")} data-testid="lead-new-last-name" className={inputClass} />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Unternehmen">
            <input type="text" value={form.company} onChange={update("company")} data-testid="lead-new-company" className={inputClass} />
          </Field>
          <Field label="Rolle / Funktion">
            <input type="text" value={form.role} onChange={update("role")} data-testid="lead-new-role" className={inputClass} />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="E-Mail">
            <input type="email" value={form.email} onChange={update("email")} data-testid="lead-new-email" className={inputClass} />
          </Field>
          <Field label="Telefon">
            <input type="tel" value={form.phone} onChange={update("phone")} data-testid="lead-new-phone" className={inputClass} />
          </Field>
        </FieldRow>
        <Field label="Website">
          <input type="url" value={form.website} onChange={update("website")} data-testid="lead-new-website" className={inputClass} />
        </Field>
      </FormSection>

      <FormSection title="2. Lead-Einordnung" testId="lead-new-section-classification">
        <FieldRow>
          <Field label="Leadtyp" required>
            <select value={form.kind} onChange={update("kind")} data-testid="lead-new-kind" className={inputClass}>
              {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Quelle" required>
            <select value={form.source_type} onChange={update("source_type")} data-testid="lead-new-source-type" className={inputClass}>
              {SOURCE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </FieldRow>
        <Field label="Quelle Zusatz">
          <input type="text" value={form.source_label} onChange={update("source_label")} data-testid="lead-new-source-label" className={inputClass} />
        </Field>
        <FieldRow>
          <Field label="Region / Ort">
            <input type="text" value={form.city_region} onChange={update("city_region")} data-testid="lead-new-region" className={inputClass} />
          </Field>
          <Field label="Systeminteresse">
            <input type="text" value={form.system_interests} onChange={update("system_interests")} placeholder="PVRA, AR_ENA, …" data-testid="lead-new-systems" className={inputClass} />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Zielgruppe">
            <input type="text" value={form.target_group} onChange={update("target_group")} data-testid="lead-new-target" className={inputClass} />
          </Field>
          <Field label="Zeitplan / Timing">
            <input type="text" value={form.timeline} onChange={update("timeline")} data-testid="lead-new-timeline" className={inputClass} />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Priorität">
            <select value={form.priority} onChange={update("priority")} data-testid="lead-new-priority" className={inputClass}>
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Tags" hint={'Komma-getrennt, z.B. „messe-leipzig, vip"'}>
            <input type="text" value={form.tags_input} onChange={update("tags_input")} data-testid="lead-new-tags" className={inputClass} />
          </Field>
        </FieldRow>
      </FormSection>

      {showStandort ? (
        <FormSection title="3. Standortdaten" testId="lead-new-section-standort">
          <FieldRow>
            <Field label="Hat Fläche?">
              <select value={form.has_location} onChange={update("has_location")} className={inputClass}>
                <option value="">—</option>
                <option value="yes">Ja</option>
                <option value="no">Nein</option>
                <option value="multiple_options">Mehrere Optionen</option>
                <option value="early_stage">Sehr früh</option>
              </select>
            </Field>
            <Field label="Standorttyp">
              <input type="text" value={form.location_type} onChange={update("location_type")} className={inputClass} />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Verfügbare Fläche (m²)">
              <input type="text" value={form.available_area} onChange={update("available_area")} className={inputClass} />
            </Field>
            <Field label="Raumhöhe">
              <input type="text" value={form.ceiling_height} onChange={update("ceiling_height")} className={inputClass} />
            </Field>
          </FieldRow>
          <Field label="Aktueller Zustand">
            <input type="text" value={form.current_status} onChange={update("current_status")} className={inputClass} />
          </Field>
        </FormSection>
      ) : null}

      {showGruender ? (
        <FormSection title="3. Gründerprofil" testId="lead-new-section-gruender">
          <FieldRow>
            <Field label="Wunschregion">
              <input type="text" value={form.wish_region} onChange={update("wish_region")} className={inputClass} />
            </Field>
            <Field label="Gründer-Rolle">
              <select value={form.founder_role} onChange={update("founder_role")} className={inputClass}>
                <option value="">—</option>
                <option value="operate">Operieren</option>
                <option value="invest">Investieren</option>
                <option value="develop">Entwickeln</option>
                <option value="partner">Partner</option>
                <option value="open">Offen</option>
              </select>
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Betreibererfahrung">
              <select value={form.operator_experience} onChange={update("operator_experience")} className={inputClass}>
                <option value="">—</option>
                <option value="yes">Ja</option>
                <option value="partial">Teilweise</option>
                <option value="no">Nein</option>
              </select>
            </Field>
            <Field label="Standortsuche">
              <select value={form.location_search_status} onChange={update("location_search_status")} className={inputClass}>
                <option value="">—</option>
                <option value="active">Aktiv</option>
                <option value="not_started">Nicht gestartet</option>
                <option value="multiple">Mehrere Optionen</option>
                <option value="open">Offen</option>
              </select>
            </Field>
          </FieldRow>
        </FormSection>
      ) : null}

      {showDev || showSales ? (
        <FormSection
          title="3. Projekt-Kontext"
          subtitle="Optional — wird in späteren Batches erweitert."
          testId="lead-new-section-dev-sales"
        >
          <Field label="Produkt / Projekt / Netzwerk">
            <input type="text" value={form.product_interest} onChange={update("product_interest")} className={inputClass} />
          </Field>
          <Field label="Beschreibung / Nachricht">
            <textarea value={form.message} onChange={update("message")} className={textareaClass} />
          </Field>
        </FormSection>
      ) : null}

      <FormSection
        title="4. Gesprächsnotiz"
        subtitle="Pflicht: Gesprächsnotiz oder nächster Schritt."
        testId="lead-new-section-note"
      >
        <Field label="Gesprächsnotiz">
          <textarea value={form.note_text} onChange={update("note_text")} data-testid="lead-new-note" className={textareaClass} />
        </Field>
      </FormSection>

      <FormSection title="5. Nächster Schritt / Follow-up" testId="lead-new-section-followup">
        <Field label="Nächster Schritt">
          <input type="text" value={form.next_step} onChange={update("next_step")} data-testid="lead-new-next-step" className={inputClass} />
        </Field>
        <FieldRow>
          <Field label="Follow-up Titel">
            <input type="text" value={form.follow_up_title} onChange={update("follow_up_title")} data-testid="lead-new-follow-up-title" className={inputClass} />
          </Field>
          <Field label="Follow-up Datum / Uhrzeit">
            <input type="datetime-local" value={form.follow_up_at} onChange={update("follow_up_at")} data-testid="lead-new-follow-up-at" className={inputClass} />
          </Field>
        </FieldRow>
      </FormSection>

      <div className="sticky bottom-0 -mx-4 flex flex-col gap-2 border-t border-white/[0.06] bg-[#050607]/95 p-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-end sm:rounded-md sm:border">
        <Link
          to="/admin/journey/inbox"
          className="inline-flex items-center justify-center rounded border border-white/10 px-4 py-2 text-xs text-zinc-400 transition hover:border-white/20 hover:text-zinc-100"
          data-testid="lead-new-cancel"
        >
          Abbrechen
        </Link>
        <button
          type="button"
          disabled={submitting}
          onClick={() => submit("inbox")}
          data-testid="lead-new-save"
          className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-4 py-2 text-xs text-zinc-200 transition hover:border-white/20 hover:bg-white/5 disabled:opacity-40"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Lead speichern
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => submit("detail")}
          data-testid="lead-new-save-detail"
          className="inline-flex items-center justify-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-medium text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
        >
          Speichern & öffnen
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
